import { FontType } from '@modusign/react-signing/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import getFontFaceFontFamily from '@app/features/apply-react-signing/components/FieldRenderer/libs/field-font-family';
import TextSpaceObserver from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/UniversalTextField/TextSpaceObserver';
import { AbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import {
  pageViewportSizesSelector,
  viewerScaleSelector,
} from '@app/libs/reactPdfJs/store/selectors';
import ptToPx from '@app/libs/unitConverters/ptToPx';

interface ObserverProps {
  fontFamily: FontType;
  fontSize: number;
  height: number;
  id: string;
  page: number;
  text: string;
  width: number;
  onObserve: (params: { id: string; isOverflow: boolean }) => void;
}

function Observer({
  fontFamily = 'NOTO_SANS',
  fontSize = 0,
  height,
  id,
  page,
  text,
  width,
  onObserve,
}: ObserverProps) {
  const [targetText, setTargetText] = useState<string | null>(null);
  const pageViewportSizes = useSelector(pageViewportSizesSelector);
  const scale = useSelector(viewerScaleSelector);

  const handleObserve = useCallback(
    (remainSpace?: AbsolutePoint) => {
      if (remainSpace) {
        /**
         * textarea 한 줄이 가득찬 상태에서 줄의 끝에 공백을 추가하면
         * 실제로 영향을 주지 않지만 넘침으로 판정하는 이슈로 인해
         * 임시로 x축 넘침 검사를 제거합니다.
         *
         * 특정 문자 . , / () 등의 주로 반각문자가 넘침을 일으킬 수 있어서
         * 추후 알맞은 x축 검사 방법을 고민해야될 것 같습니다.
         * @see: https://github.com/modusign/modusign-client/issues/3790
         */
        const isOverflow = remainSpace.y < 0;
        onObserve({ id, isOverflow });
      }
    },
    [id, onObserve]
  );

  /**
   * TextSpaceObserver componentDidUpdate 발생시키려고 의도적으로 props를 변경합니다.
   */
  useEffect(() => {
    setTargetText(text);
  }, [text]);

  const viewport = useMemo(() => {
    if (pageViewportSizes && scale && page > 0) {
      return { ...pageViewportSizes[page - 1], scale };
    }
    return null;
  }, [page, pageViewportSizes, scale]);

  if (!viewport) {
    return null;
  }

  return (
    <TextSpaceObserver
      fontSizes={[ptToPx(fontSize * viewport.scale)]}
      style={{
        fontFamily: getFontFaceFontFamily(fontFamily),
        height: viewport.height * height,
        lineHeight: 1,
        textAlign: 'left',
        width: viewport.width * width,
        letterSpacing: 0,
      }}
      text={targetText}
      onObserve={handleObserve}
    />
  );
}

export default Observer;
